import { FC, useEffect, useState } from 'react'
import { Save, Settings as SettingsIcon } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Center } from '@/js/editor/components/general/settings/Center.tsx'
import { Zoom } from '@/js/editor/components/general/settings/Zoom.tsx'
import { useMap } from 'react-leaflet'
import { useMaps } from '@/js/editor/hooks/api/useMaps.ts'
import { LatLng, LatLngExpression } from 'leaflet'
import { Label } from '@/components/ui/label.tsx'
import { Checkbox } from '@/components/ui/checkbox.tsx'
import { convertToLatLng } from '@/js/helper/convertToLatLng.ts'
import { ColorPicker } from '@/js/editor/components/general/inputs/ColorPicker.tsx'
import { useAtomValue } from 'jotai'
import { mapDataAtom } from '@/js/editor/state/mapAtoms.ts'
import { toast } from 'sonner'

const wrapCoordinates = (center: LatLngExpression): LatLngExpression => {
    if (Array.isArray(center)) {
        const latLng = new LatLng(center[0], center[1])
        const wrapped = latLng.wrap()
        return [wrapped.lat, wrapped.lng]
    } else if (center instanceof LatLng) {
        return center.wrap()
    }
    return center
}

export const SettingsPopover: FC = () => {
    const [isOpen, setIsOpen] = useState(false)
    const {
        zoom: mapZoom,
        center: mapCenter,
        name: mapName,
        uses_tags_as_categories,
        map_type,
        show_short_description_in_sidebar,
        colors: mapColors,
    } = useAtomValue(mapDataAtom)

    const [center, setCenter] = useState(mapCenter)
    const [zoom, setZoom] = useState(mapZoom)
    const [isCategorized, setIsCategorized] = useState(uses_tags_as_categories)
    const [showShortDescriptionInSidebar, setShowShortDescriptionInSidebar] = useState(
        show_short_description_in_sidebar
    )
    const [colors, setColors] = useState(mapColors)

    const map = useMap()
    const { update: saveMapSettings } = useMaps()

    useEffect(() => {
        if (isOpen && zoom && center) {
            map.setView(convertToLatLng(center), zoom, {
                animate: true,
                duration: 1,
                easeLinearity: 0.5,
            })
        }
    }, [isOpen, center])

    useEffect(() => {
        const handleZoomEnd = () => setZoom(map.getZoom())
        map.on('zoomend', handleZoomEnd)

        return () => {
            map.off('zoomend', handleZoomEnd)
        }
    }, [map])

    return (
        <Popover
            open={isOpen}
            onOpenChange={(open) => {
                if (open === true) setIsOpen(open)
            }}
        >
            <PopoverTrigger>
                <div
                    className={
                        'absolute bottom-0 ring ring-mm-border left-0 m-5 w-12 bg-mm-bg3 shadow-md hover:shadow-xs rounded-md hover:bg-mm-bg2 cursor-pointer p-2 transition-all transition-400'
                    }
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <SettingsIcon size={30} />
                </div>
            </PopoverTrigger>
            <PopoverContent id={'settings-popover'} className={'z-2001 absolute bottom-18 -left-25 w-60'}>
                <div className="flex flex-col gap-6">
                    <Center
                        mapType={map_type}
                        onChange={(newCenter) => {
                            const wrappedCenter = map_type == 'geographic' ? wrapCoordinates(newCenter) : newCenter
                            setCenter([wrappedCenter[0], wrappedCenter[1]])
                        }}
                    />

                    <Zoom
                        zoom={zoom}
                        max={map.getMaxZoom()}
                        onChange={(newZoom) => {
                            setZoom(newZoom)
                            map.setZoom(newZoom, {
                                animate: true,
                                duration: 1,
                                easeLinearity: 0.5,
                            })
                        }}
                    />

                    <div className="h-px w-full bg-mm-border" />

                    <div className={'flex flex-col space-x-5 justify-between'}>
                        <Label>Kategorien</Label>
                        <div className={'flex justify-between space-x-5 text-slate-400'}>
                            <span className={'text-sm'}>Karteneinträge gruppieren</span>
                            <Checkbox
                                name="categorized_view"
                                onCheckedChange={(checked) => {
                                    setIsCategorized(!!checked)
                                }}
                                checked={isCategorized ?? false}
                            />
                        </div>
                    </div>

                    <div className={'flex flex-col space-x-5 justify-between'}>
                        <Label>Beschreibung</Label>
                        <div className={'flex justify-between space-x-5 text-slate-400'}>
                            <span className={'text-sm'}>Kurzbeschreibung auch in Seitenleiste anzeigen</span>
                            <Checkbox
                                name="categorized_view"
                                onCheckedChange={(checked) => {
                                    setShowShortDescriptionInSidebar(!!checked)
                                }}
                                checked={showShortDescriptionInSidebar ?? false}
                            />
                        </div>
                    </div>

                    <div className="h-px w-full bg-mm-border" />

                    <div className={'flex flex-row flex-wrap gap-3'}>
                        <Label>Primärfarbe</Label>
                        <ColorPicker
                            label={'Primärfarbe'}
                            labelStyle={'font-light'}
                            value={colors?.primary}
                            onChange={(color) => setColors({ ...colors, primary: color })}
                            zIndex={2002}
                        />

                        <ColorPicker
                            label={'Hintergrund'}
                            labelStyle={'font-light'}
                            value={colors?.background}
                            onChange={(color) => setColors({ ...colors, background: color })}
                            zIndex={2002}
                        />
                    </div>

                    <div className={'flex flex-row flex-wrap gap-3'}>
                        <Label>Standardfarben für Markierungen</Label>
                        <ColorPicker
                            label={'Farbe'}
                            labelStyle={'font-light'}
                            value={colors?.standard}
                            onChange={(color) => setColors({ ...colors, standard: color })}
                            zIndex={2002}
                        />

                        <ColorPicker
                            label={'Akzentfarbe'}
                            labelStyle={'font-light'}
                            value={colors?.highlight}
                            onChange={(color) => setColors({ ...colors, highlight: color })}
                            zIndex={2002}
                        />
                    </div>

                    <div className={'flex flex-row justify-between text-sm'}>
                        <Button
                            variant={'secondary'}
                            onClick={() => {
                                const mapContainer = document.querySelector('.leaflet-container')
                                const menu = document.getElementById('mm-menu')
                                mapContainer.classList.remove('!cursor-crosshair')
                                menu?.classList.remove('center-selection-active')

                                setIsOpen(false)
                            }}
                        >
                            Abbrechen
                        </Button>
                        <Button
                            onClick={async () => {
                                try {
                                    await saveMapSettings({
                                        name: mapName,
                                        zoom,
                                        center,
                                        uses_tags_as_categories: isCategorized,
                                        show_short_description_in_sidebar: showShortDescriptionInSidebar,
                                        colors: {
                                            primary: colors.primary,
                                            standard: colors.standard,
                                            highlight: colors.highlight,
                                            background: colors.background,
                                        },
                                    })

                                    setIsOpen(false)
                                    toast.success('Karteneinstellungen gespeichert.')
                                } catch (error) {
                                    toast.error('Speichern fehlgeschlagen', {
                                        description:
                                            error instanceof Error
                                                ? error.message
                                                : 'Die Karteneinstellungen konnten nicht gespeichert werden.',
                                    })
                                }
                            }}
                        >
                            <Save />
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
